import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import type { FieldProps, InteractionProps } from "@hopper-ui/components";
import { useMemo, type ReactElement } from "react";
import { useController, type Control, type ControllerFieldState, type ControllerRenderProps, type FieldPath, type FieldValues, type UseFormStateReturn } from "react-hook-form";

interface ControllerRenderWithIdProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> extends ControllerRenderProps<TFieldValues, TName>,
    Pick<FieldProps, "errorMessage">,
    Pick<InteractionProps, "isInvalid"> {
    id: TName;
}

interface FormFieldProps<TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>> {
    /**
   * The control object provided by useForm from React Hook Form, which enables form inference.
   */
    control: Control<TFieldValues>;
    /**
   * The name (key) of the field, which should be one of the fields declared in useForm<FormType>.
   */
    name: TName;
    /**
   * A render function that receives the field, fieldState, and formState from useController, and returns a React element.
   *
   * This function has been extracted from the Controller component of React Hook Form.
   *
   * @see https://react-hook-form.com/docs/usecontroller/controller
   */
    render: ({ field, fieldState, formState }: {
        field: ControllerRenderWithIdProps<TFieldValues, TName>;
        fieldState: ControllerFieldState;
        formState: UseFormStateReturn<TFieldValues>;
    }) => ReactElement;
}

/**
 * FormField manages the field's state using useController from React Hook Form.
 */
export const FormField = <TFieldValues extends FieldValues = FieldValues, TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>>(props: FormFieldProps<TFieldValues, TName>) => {
    const { tShared } = useSharedTranslation();
    const { name, control, render } = props;

    const { field, fieldState, formState } = useController({ name, control });

    const error = useMemo(() => fieldState.error, [fieldState.error]);

    const errorMessage = useMemo(() => {
        const message = error?.message;

        // If the error message is a translation key, translate it
        if (message && tShared(message) !== message) {
            return tShared(message);
        }

        // Otherwise, return the message as is
        return message;
    }, [error, tShared]);

    const fieldWithId = useMemo(() => ({ ...field, id: name, errorMessage, isInvalid: !!errorMessage }), [field, name, errorMessage]);

    return (
        render({ field: fieldWithId, fieldState, formState })
    );
};
